import React, { useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from './store';
import { useAuthUser, useUserProfile } from './hooks';
import type { User } from '@supabase/supabase-js';

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { setUser, setProfile } = useAuthStore();
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null>(null);

  const { data: user } = useAuthUser();
  const { data: profile } = useUserProfile(user?.id);

  useEffect(() => {
    if (user?.id !== lastUserIdRef.current) {
      setUser(user || null);
      lastUserIdRef.current = user?.id ?? null;
    }
  }, [user, setUser]);

  useEffect(() => {
    setProfile(profile || null);
  }, [profile, setProfile]);

  useEffect(() => {
    if (!supabase) return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;

      const currentUser = session?.user ?? null;
      const currentUserId = currentUser?.id ?? null;

      const cachedUser = queryClient.getQueryData<User | null>(['auth', 'user']);
      if (cachedUser?.id === currentUserId) return;

      queryClient.setQueryData(['auth', 'user'], currentUser, {
        updatedAt: Date.now(),
      });
      setUser(currentUser);
      lastUserIdRef.current = currentUserId;

      if (currentUser) {
        if (event === 'SIGNED_IN' || (event as string) === 'SIGNED_UP') {
          queryClient.invalidateQueries({ queryKey: ['auth', 'profile', currentUser.id] });
        }
      } else {
        queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient, setUser, setProfile]);

  return <>{children}</>;
}
